import React, { useState } from 'react';
import { formatCurrency, useLocale } from '@unisporkal/localization';
import useOnLoad from '../../../hooks/queries/useOnLoad';
import { useAsset } from '../../../hooks/stateful/useAsset';
import useSelectedStandardLicenseSize from '../../hooks/useSelectedStandardLicenseSize';
import useAvailableSizes from '../../hooks/useAvailableSizes';
import useSelectedSubscriptionOption, {
  type SubscriptionOption,
} from '../../hooks/useSelectedSubscriptionOption';
import useSelectedUltraPack, {
  defaultSelectedPack,
} from '../../hooks/useSelectedUltraPack';
import useSelectPricing, {
  SINGLE_IMAGE_PACK_SIZE,
} from '../../hooks/useSelectPricing';
import {
  type IncludedSize,
  type Price,
  type SubscriptionLicenseConfig,
} from '../../../../types/isomorphic';
import { ALCBuyCard, type AlcAssetType, type SizeOption } from './ALCBuyCard';
import { UltrapackBuyCard } from './UltrapackBuyCard';
import {
  SubscriptionBuyCard,
  type SubscriptionTier,
} from './SubscriptionBuyCard';
import { isEps } from '../../../../shared/utils/sizeRank';
import styles from './OfferBuyCards.module.scss';

type SubscriptionOfferCardId = 'alc' | 'ultrapack' | 'subscription';

const CARD_ID_BY_OPTION: Record<SubscriptionOption, SubscriptionOfferCardId> = {
  single: 'alc',
  ultrapack: 'ultrapack',
  subscription: 'subscription',
  customlicense: 'subscription',
};

const resolveInitialExpandedCard = (
  option: SubscriptionOption,
  includeSubscription: boolean
): SubscriptionOfferCardId => {
  const cardId = CARD_ID_BY_OPTION[option];
  return cardId === 'subscription' && !includeSubscription
    ? 'ultrapack'
    : cardId;
};

const toAlcAssetType = (assetType?: string): AlcAssetType =>
  !assetType || assetType.toLowerCase() === 'image' ? 'image' : 'video';

const toIncludedSizesTranslationKey = (
  assetType: AlcAssetType,
  isVector: boolean | undefined,
  sizes: IncludedSize[]
) => {
  if (assetType === 'video') {
    return sizes.some((size) => size.definition === '4K')
      ? 'includes_all_formats_and_4k'
      : 'includes_all_formats';
  }

  return isVector || sizes.some((size) => size.name === 'eps')
    ? 'includes_all_sizes_formats'
    : 'includes_all_sizes';
};

const toSubscriptionTier = (
  type: SubscriptionLicenseConfig['type']
): SubscriptionTier =>
  type === 'editorial-creative' ? 'editorial' : 'creative';

const MEDIUM_TEE_SHIRT_SIZES = ['Medium', 'M'];

const toAlcSizeToggleSelection = (size?: Price) => {
  if (size && isEps(size)) return '1_alc_eps_toggle';
  if (!size?.teeShirtSize) return '';
  if (MEDIUM_TEE_SHIRT_SIZES.includes(size.teeShirtSize))
    return '1_alc_medium_toggle';
  return '1_alc_large_toggle';
};

const toOfferToggleSelection =
  (alcToggleSelection: string) => (packSize: number) =>
    packSize === SINGLE_IMAGE_PACK_SIZE
      ? alcToggleSelection
      : `${packSize}_up_toggle`;

// The subscription card carries the headline savings badge; when it's absent,
// the UltraPack card takes over that role — where its pricing earns it.
const shouldShowUltrapackSavingsBadge = (
  includeSubscription: boolean,
  config: SubscriptionLicenseConfig
) => !includeSubscription && !!config.isUltraPackSavingsBadgeEligible;

const toSizeOptions = (sizes: Price[]): SizeOption[] =>
  sizes.map((size) => ({
    name: size.name,
    label: size.label,
    price:
      size.discountPrice || (typeof size.price === 'string' ? size.price : ''),
    teeShirtSize: size.teeShirtSize,
    pixelWidth: size.pixelWidth,
    pixelHeight: size.pixelHeight,
    localeWidth: size.localeWidth,
    localeHeight: size.localeHeight,
    unit: size.unit,
    dpi: size.dpi,
    megapixels: size.megapixels,
  }));

type OfferBuyCardsProps = {
  includeSubscription?: boolean;
};

const Container = ({ includeSubscription = true }: OfferBuyCardsProps) => {
  const locale = useLocale();
  const { asset } = useAsset();
  const { response } = useOnLoad();
  const loadedAsset = response.asset;
  const availableSizes = useAvailableSizes();
  const { sizes, selectedSize, selectSizeByName } =
    useSelectedStandardLicenseSize();
  const { selectedSubscriptionOption } = useSelectedSubscriptionOption();
  const { selectedUltraPack } = useSelectedUltraPack();
  const {
    selectPricing,
    selectSingleImagePricing,
    selectSubscriptionPricing,
    trackToggle,
  } = useSelectPricing({
    toToggleSelection: toOfferToggleSelection(
      toAlcSizeToggleSelection(selectedSize)
    ),
  });
  const [expandedCard, setExpandedCard] = useState<SubscriptionOfferCardId>(
    () =>
      resolveInitialExpandedCard(
        selectedSubscriptionOption,
        includeSubscription
      )
  );

  const subConfig = response.config.config as
    | SubscriptionLicenseConfig
    | undefined;

  if (!subConfig) {
    return null;
  }

  const format = (amount: number) =>
    formatCurrency(amount, locale, subConfig.currency).formattedPrice;

  const selectedPackOption = subConfig.ultraPackOptions.find(
    (option) => option.packSize === selectedUltraPack
  );
  const displayedPackOption =
    selectedPackOption ?? subConfig.ultraPackOptions[0];
  const displayedPackSize =
    displayedPackOption?.packSize ?? defaultSelectedPack;
  const alcAssetType = toAlcAssetType(asset?.assetType);
  const hasEditorialLicense = loadedAsset?.hasEditorialLicense ?? false;

  const selectAlcSize = (name: string) => {
    selectSizeByName(name);
    trackToggle(
      toAlcSizeToggleSelection(sizes.find((size) => size.name === name))
    );
  };

  return (
    <div className={styles.offerBuyCards}>
      <ALCBuyCard
        sizeOptions={toSizeOptions(sizes)}
        selectedSize={selectedSize?.name}
        onSelectSize={selectAlcSize}
        assetType={alcAssetType}
        includedSizes={availableSizes}
        includedSizesTranslationKey={toIncludedSizesTranslationKey(
          alcAssetType,
          asset?.isVector,
          availableSizes
        )}
        isEditorial={hasEditorialLicense}
        isSelected={selectedSubscriptionOption === 'single'}
        onSelect={selectSingleImagePricing}
        isExpanded={expandedCard === 'alc'}
        onExpand={() => setExpandedCard('alc')}
      />
      <UltrapackBuyCard
        packOptions={subConfig.ultraPackBuyCardOptions ?? []}
        selectedPackSize={selectedUltraPack}
        onSelectPack={selectPricing}
        isEditorial={hasEditorialLicense}
        showSavingsBadge={shouldShowUltrapackSavingsBadge(
          includeSubscription,
          subConfig
        )}
        isSelected={selectedSubscriptionOption === 'ultrapack'}
        onSelect={() => selectPricing(displayedPackSize)}
        isExpanded={expandedCard === 'ultrapack'}
        onExpand={() => setExpandedCard('ultrapack')}
      />
      {includeSubscription && (
        <SubscriptionBuyCard
          tier={toSubscriptionTier(subConfig.type)}
          pricePerImage={format(subConfig.pricePerDownload)}
          total={format(subConfig.pricePerMonth)}
          showSavingsBadge={!!subConfig.isSubscriptionSavingsBadgeEligible}
          isSelected={selectedSubscriptionOption === 'subscription'}
          onSelect={() => selectSubscriptionPricing(subConfig.sku)}
          isExpanded={expandedCard === 'subscription'}
          onExpand={() => setExpandedCard('subscription')}
        />
      )}
    </div>
  );
};

export default Container;
